import { useEffect, useMemo, useState } from 'react';
import { onActiveOrdersForDaySnapshot } from '@paulownia/shared/services/orders';
import { OrderDetailModal } from '../orders/OrderDetailModal';
import { OrderEditDialog } from '../orders/OrderEditDialog';
import { useOrderManager } from '@paulownia/shared/hooks/useOrderManager';
import { useProductLookup } from '@paulownia/shared/hooks/useProductLookup';
import { getCachedProducts } from '@paulownia/shared/services/products-cache';
import { getTenantById } from '@paulownia/shared/services/tenant';
import { reportError } from '@paulownia/shared/lib/error-reporting';
import { type Order, type Product, type OrderLineOption, type SaleMode, getOptionLabels, getPortionSize, formatQuantityDisplay, getLineCompositionSelections } from '@paulownia/shared/types';
import { Button } from '@paulownia/shared/components/ui/button';
import { Badge } from '@paulownia/shared/components/ui/badge';
import { Input } from '@paulownia/shared/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@paulownia/shared/components/ui/tabs';
import { ChevronDown, ChevronUp, Clock, User, Package } from 'lucide-react';
import { getTodayKey, getPreviousDay, getNextDay } from '@paulownia/shared/lib/date-utils';
import { useTenant } from '@paulownia/shared/lib/tenant-context';

// Helper pour formater les poids en kg sans zéros inutiles (ex: 0.500 -> 0.5)
function formatKg(num: number): string {
  const rounded = Math.round(num * 1000) / 1000;
  return rounded.toString();
}

interface Reservation {
  orderId: string;
  lineIndex: number;
  customerName: string;
  pickupTime: string;
  pickupAtISO: string;
  productId: string;
  quantity: number;
  preparedQuantity: number;
  options: OrderLineOption[];
  status: string;
  paid: boolean;
  portionSizeId?: string;
}

// Tracks quantity breakdown by portion size
interface PortionBreakdown {
  portionSizeId: string | undefined;
  label: string;
  count: number;  // Number of orders with this portion
  total: number;  // Total quantity for this portion
}

interface ProductAggregate {
  productId: string;
  productName: string;
  section: string;
  total: number;
  prepared: number;
  reservations: Reservation[];
  portionBreakdowns: PortionBreakdown[];  // Breakdown by portion size
  fromComposition: number;  // Quantity that comes from composition orders (coffrets)
}

export function ProductsStatus() {
  const { currentTenantId } = useTenant();
  const [orders, setOrders] = useState<Order[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedDate, setSelectedDate] = useState(getTodayKey());

  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set());
  const [viewingOrderId, setViewingOrderId] = useState<string | null>(null);
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);
  const [tenantSettings, setTenantSettings] = useState<{
    sectionOrder?: Record<string, number>;
    subSectionOrder?: Record<string, Record<string, number>>;
  } | null>(null);

  const {
    handleStatusChange,
    handleTogglePaid,
    handleMarkAllPreparedAndReady,
    toggleLinePrepared
  } = useOrderManager();

  const viewingOrder = useMemo(() =>
    orders.find(o => o.id === viewingOrderId) || null
    , [orders, viewingOrderId]);

  useEffect(() => {
    if (!currentTenantId) {
      setOrders([]);
      return;
    }
    // Use server-side filtering to exclude 'done' orders
    const unsub = onActiveOrdersForDaySnapshot(selectedDate, (o) => {
      setOrders(o); // Already filtered server-side
    }, currentTenantId);
    return () => unsub();
  }, [selectedDate, currentTenantId]);

  useEffect(() => {
    if (!currentTenantId) {
      setProducts([]);
      return;
    }

    // Initial load with cache
    getCachedProducts(currentTenantId).then(setProducts).catch((error) => {
      reportError(error, { context: 'ProductsStatus.loadProducts', tenantId: currentTenantId });
    });

    // Refresh every 5 minutes
    const interval = setInterval(() => {
      getCachedProducts(currentTenantId).then(setProducts).catch((error) => {
        reportError(error, { context: 'ProductsStatus.refreshProducts', tenantId: currentTenantId });
      });
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [currentTenantId]);

  // Load tenant settings for section/subSection ordering
  useEffect(() => {
    if (!currentTenantId) {
      setTenantSettings(null);
      return;
    }
    getTenantById(currentTenantId)
      .then((tenant) => {
        if (tenant) {
          setTenantSettings(tenant.settings || null);
        }
      })
      .catch((error) => {
        reportError(error, { context: 'ProductsStatus.loadTenantSettings', tenantId: currentTenantId });
      });
  }, [currentTenantId]);

  // Use the optimized product lookup hook (O(1) instead of O(n))
  const { getName: getProductName, getSaleMode: getProductSaleMode, getSection: getProductSection } = useProductLookup(products);

  // Helper pour arrondir à 3 décimales et éviter les problèmes d'arrondi JS
  const roundTo3Decimals = (num: number): number => {
    return Math.round(num * 1000) / 1000;
  };

  // Helper to get portion label from product
  const getPortionLabel = (productId: string, portionSizeId?: string): string => {
    if (!portionSizeId) return 'Standard';
    const product = products.find(p => p.id === productId);
    if (!product) return portionSizeId;
    const portion = getPortionSize(product, portionSizeId);
    return portion?.label || portionSizeId;
  };

  const aggregates = useMemo(() => {
    const map = new Map<string, ProductAggregate>();

    // Helper to add or update an aggregate
    const addToAggregate = (
      productId: string,
      quantity: number,
      preparedQuantity: number,
      reservation: Reservation | null,
      portionSizeId: string | undefined,
      isFromComposition: boolean
    ) => {
      const existing = map.get(productId);

      if (existing) {
        existing.total = roundTo3Decimals(existing.total + quantity);
        existing.prepared = roundTo3Decimals(existing.prepared + preparedQuantity);
        if (isFromComposition) {
          existing.fromComposition = roundTo3Decimals(existing.fromComposition + quantity);
        }
        if (reservation) {
          existing.reservations.push(reservation);
        }

        // Update portion breakdown (only for direct orders, not compositions)
        if (!isFromComposition) {
          const portionKey = portionSizeId || '__standard__';
          const existingPortion = existing.portionBreakdowns.find(
            pb => (pb.portionSizeId || '__standard__') === portionKey
          );
          if (existingPortion) {
            existingPortion.count += 1;
            existingPortion.total = roundTo3Decimals(existingPortion.total + quantity);
          } else {
            existing.portionBreakdowns.push({
              portionSizeId: portionSizeId,
              label: getPortionLabel(productId, portionSizeId),
              count: 1,
              total: quantity,
            });
          }
        }
      } else {
        map.set(productId, {
          productId,
          productName: getProductName(productId),
          section: getProductSection(productId) || 'Autres',
          total: roundTo3Decimals(quantity),
          prepared: roundTo3Decimals(preparedQuantity),
          reservations: reservation ? [reservation] : [],
          portionBreakdowns: !isFromComposition ? [{
            portionSizeId: portionSizeId,
            label: getPortionLabel(productId, portionSizeId),
            count: 1,
            total: quantity,
          }] : [],
          fromComposition: isFromComposition ? quantity : 0,
        });
      }
    };

    for (const order of orders) {
      order.lines.forEach((line, lineIndex) => {
        const pickupDate = new Date(order.pickupAtISO);
        const product = products.find(p => p.id === line.productId);

        const reservation: Reservation = {
          orderId: order.id,
          lineIndex,
          customerName: order.customerName,
          pickupTime: pickupDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
          pickupAtISO: order.pickupAtISO,
          productId: line.productId,
          quantity: line.quantity,
          preparedQuantity: line.preparedQuantity || 0,
          options: line.options || [],
          status: order.status,
          paid: order.paid,
          portionSizeId: line.portionSizeId,
        };

        // Add the direct order line
        addToAggregate(
          line.productId,
          line.quantity,
          line.preparedQuantity || 0,
          reservation,
          line.portionSizeId,
          false
        );

        // Check for composition selections and aggregate those products too
        if (product) {
          const compositionSelections = getLineCompositionSelections(line, product);
          for (const selection of compositionSelections) {
            if (selection.quantity > 0) {
              // Add composition items to the aggregate (multiplied by line quantity for multiple coffrets)
              const totalQty = selection.quantity * line.quantity;
              addToAggregate(
                selection.productId,
                totalQty,
                0, // Composition items don't track prepared separately
                null, // No reservation entry for composition items
                undefined,
                true
              );
            }
          }
        }
      });
    }

    // Sort reservations by pickup time within each product
    for (const agg of map.values()) {
      agg.reservations.sort((a, b) => a.pickupAtISO.localeCompare(b.pickupAtISO));
    }

    // Sort by section then by product name
    return Array.from(map.values()).sort((a, b) => {
      if (a.section !== b.section) {
        return a.section.localeCompare(b.section);
      }
      return a.productName.localeCompare(b.productName);
    });
    // getProductName, getProductSection, getPortionLabel are derived from products
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, products]);

  const toggleExpanded = (productId: string) => {
    setExpandedProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  const isToday = selectedDate === getTodayKey();

  // Extract unique sections dynamically
  const sections = useMemo(() => {
    const uniqueSections = new Set<string>();
    aggregates.forEach(agg => {
      if (agg.section) {
        uniqueSections.add(agg.section);
      }
    });
    return Array.from(uniqueSections).sort();
  }, [aggregates]);

  // Group products by section
  const productsBySection = useMemo(() => {
    const grouped: Record<string, ProductAggregate[]> = {};
    sections.forEach(section => {
      grouped[section] = aggregates.filter(a => a.section === section);
    });
    return grouped;
  }, [aggregates, sections]);

  // Section display configuration
  const getSectionConfig = (section: string) => {
    const configs: Record<string, { emoji: string; color: string; bgColor: string }> = {
      'rotisserie': { emoji: '🍗', color: 'text-orange-700', bgColor: 'bg-orange-100' },
      'traiteur': { emoji: '🥗', color: 'text-emerald-700', bgColor: 'bg-emerald-100' },
      'boucherie': { emoji: '🥩', color: 'text-red-700', bgColor: 'bg-red-100' },
      'patisserie': { emoji: '🍰', color: 'text-pink-700', bgColor: 'bg-pink-100' },
      'boulangerie': { emoji: '🥖', color: 'text-amber-700', bgColor: 'bg-amber-100' },
      'volailles': { emoji: '🍗', color: 'text-orange-700', bgColor: 'bg-orange-100' },
      'accompagnements': { emoji: '🥗', color: 'text-emerald-700', bgColor: 'bg-emerald-100' },
    };
    return configs[section.toLowerCase()] || { emoji: '📦', color: 'text-blue-700', bgColor: 'bg-blue-100' };
  };



  // Marquer une ligne spécifique comme préparée dans une commande
  const onToggleLinePreparedWrapper = async (orderId: string, lineIndex: number) => {
    const order = orders.find(o => o.id === orderId);
    if (!order) return;
    await toggleLinePrepared(order, lineIndex);
  };



  const onStatusChangeWrapper = async (orderId: string, newStatus: 'ready' | 'done') => {
    await handleStatusChange(orderId, newStatus);
    if (newStatus === 'done' && viewingOrderId === orderId) {
      setViewingOrderId(null);
    }
  };

  const onTogglePaidWrapper = async (orderId: string, currentPaidStatus: boolean) => {
    await handleTogglePaid(orderId, currentPaidStatus);
  };





  // Re-implementing a specific toggler for the modal usage or reusing logic
  // Re-implementing a specific toggler for the modal usage or reusing logic
  const onModalTogglePreparedWrapper = async (order: Order, lineIndex: number) => {
    await toggleLinePrepared(order, lineIndex);
  };

  function openEditModal(order: Order) {
    setViewingOrderId(null);
    setEditingOrder(order);
  }

  return (
    <div className="pb-16">
      {/* Header */}
      <div className="px-4 py-4 border-b bg-white">
        <h1 className="text-xl font-semibold mb-3">Statut des produits</h1>

        {/* Date selector */}
        <div className="flex items-center gap-2 mb-3">
          <Button variant="outline" size="sm" onClick={() => setSelectedDate(getPreviousDay(selectedDate))}>
            ←
          </Button>
          <div className="flex-1 flex items-center gap-2">
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="h-9 w-full text-base"
              style={{ fontSize: '16px' }}
            />
            {!isToday && (
              <Button variant="default" size="sm" onClick={() => setSelectedDate(getTodayKey())}>
                Aujourd'hui
              </Button>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={() => setSelectedDate(getNextDay(selectedDate))}>
            →
          </Button>
        </div>

      </div>

      <div className="p-4 space-y-6">
        {sections.length === 0 ? (
          <div className="text-center text-sm text-gray-500 py-8">
            Aucune commande pour cette date.
          </div>
        ) : sections.length === 1 ? (
          // Single section - no tabs needed
          <div className="space-y-6">
            {sections.map((section) => {
              const config = getSectionConfig(section);
              const sectionProducts = productsBySection[section];
              return (
                <div key={section}>
                  <h2 className={`text-lg font-semibold ${config.color} mb-3 flex items-center gap-2`}>
                    <span className={`${config.bgColor} px-2 py-1 rounded`}>
                      {config.emoji} {section.charAt(0).toUpperCase() + section.slice(1)}
                    </span>
                  </h2>
                  <div className="space-y-3">
                    {sectionProducts.map((agg) => (
                      <ProductCard
                        key={agg.productId}
                        aggregate={agg}
                        isExpanded={expandedProducts.has(agg.productId)}
                        onToggle={() => toggleExpanded(agg.productId)}
                        onToggleLinePrepared={onToggleLinePreparedWrapper}
                        onOpenOrder={(orderId) => {
                          setViewingOrderId(orderId);
                        }}
                        saleMode={getProductSaleMode(agg.productId)}
                        products={products}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          // Multiple sections - use tabs
          <Tabs defaultValue={sections[0]} className="w-full">
            <TabsList className={`grid w-full mb-6`} style={{ gridTemplateColumns: `repeat(${sections.length}, minmax(0, 1fr))` }}>
              {sections.map((section) => (
                <TabsTrigger key={section} value={section}>
                  {section.charAt(0).toUpperCase() + section.slice(1)}
                </TabsTrigger>
              ))}
            </TabsList>

            {sections.map((section) => {
              const config = getSectionConfig(section);
              const sectionProducts = productsBySection[section];
              return (
                <TabsContent key={section} value={section} className="space-y-6">
                  {sectionProducts.length > 0 ? (
                    <div>
                      <h2 className={`text-lg font-semibold ${config.color} mb-3 flex items-center gap-2`}>
                        <span className={`${config.bgColor} px-2 py-1 rounded`}>
                          {config.emoji} {section.charAt(0).toUpperCase() + section.slice(1)}
                        </span>
                      </h2>
                      <div className="space-y-3">
                        {sectionProducts.map((agg) => (
                          <ProductCard
                            key={agg.productId}
                            aggregate={agg}
                            isExpanded={expandedProducts.has(agg.productId)}
                            onToggle={() => toggleExpanded(agg.productId)}
                            onToggleLinePrepared={onToggleLinePreparedWrapper}
                            onOpenOrder={(orderId) => {
                              setViewingOrderId(orderId);
                            }}
                            saleMode={getProductSaleMode(agg.productId)}
                            products={products}
                          />
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center text-sm text-gray-500 py-8">
                      Aucun produit dans cette section pour cette date.
                    </div>
                  )}
                </TabsContent>
              );
            })}
          </Tabs>
        )}
      </div>
      <OrderDetailModal
        order={viewingOrder}
        isOpen={!!viewingOrder}
        onClose={() => setViewingOrderId(null)}
        products={products}
        onStatusChange={onStatusChangeWrapper}
        onMarkAllPreparedAndReady={handleMarkAllPreparedAndReady}
        onTogglePaid={onTogglePaidWrapper}
        onTogglePrepared={onModalTogglePreparedWrapper}
        onEdit={openEditModal}
      />

      <OrderEditDialog
        order={editingOrder}
        isOpen={!!editingOrder}
        onClose={() => setEditingOrder(null)}
        products={products}
        tenantSettings={tenantSettings}
      />
    </div>
  );
}

function ProductCard({
  aggregate,
  isExpanded,
  onToggle,
  onToggleLinePrepared,
  onOpenOrder,
  saleMode,
  products,
}: {
  aggregate: ProductAggregate;
  isExpanded: boolean;
  onToggle: () => void;
  onToggleLinePrepared: (orderId: string, lineIndex: number) => void;
  onOpenOrder: (orderId: string) => void;
  saleMode: SaleMode;
  products: Product[];
}) {
  const remaining = Math.round((aggregate.total - aggregate.prepared) * 1000) / 1000;
  const remainingPositive = Math.max(remaining, 0);

  const isComplete = aggregate.prepared >= aggregate.total;

  // Check if product has portion sizes (any non-standard breakdown)
  const hasPortionSizes = aggregate.portionBreakdowns.some(pb => pb.portionSizeId !== undefined);

  // Format portion breakdown for display (e.g., "3× 250g, 2× 500g")
  const portionBreakdownText = hasPortionSizes
    ? aggregate.portionBreakdowns
        .filter(pb => pb.portionSizeId !== undefined)
        .map(pb => `${pb.total}× ${pb.label}`)
        .join(', ')
    : null;

  // Check if some items come from compositions (coffrets)
  const hasCompositionItems = aggregate.fromComposition > 0;

  return (
    <div className={`rounded-lg border-2 bg-white overflow-hidden transition-all ${isComplete ? 'border-green-300 bg-green-50/50' : 'border-gray-200'
      }`}>
      {/* Header - clickable */}
      <button
        onClick={onToggle}
        className="w-full p-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center text-xl font-bold ${isComplete
              ? 'bg-green-100 text-green-700'
              : 'bg-orange-100 text-orange-700'
              }`}>
              {/* Show order count for products with portions, otherwise show quantity */}
              {hasPortionSizes
                ? aggregate.reservations.length
                : ((saleMode === 'WEIGHT' || saleMode === 'WEIGHT_ESTIMATED') ? formatKg(aggregate.total) : aggregate.total)
              }
            </div>
            <div>
              <div className="font-semibold text-lg">{aggregate.productName}</div>
              <div className="text-sm text-gray-500 flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <span>{aggregate.reservations.length} commande{aggregate.reservations.length > 1 ? 's' : ''}</span>
                  {remainingPositive > 0 && !hasPortionSizes && (
                    <Badge variant="secondary" className="text-xs">
                      {(saleMode === 'WEIGHT' || saleMode === 'WEIGHT_ESTIMATED')
                        ? `${formatKg(remainingPositive)} kg restant${remainingPositive > 1 ? 's' : ''}`
                        : `${remainingPositive} restant${remainingPositive > 1 ? 's' : ''}`
                      }
                    </Badge>
                  )}
                  {isComplete && (
                    <Badge variant="green" className="text-xs">
                      ✓ Complet
                    </Badge>
                  )}
                </div>
                {/* Show portion breakdown */}
                {portionBreakdownText && (
                  <div className="text-xs text-orange-600 font-medium">
                    {portionBreakdownText}
                  </div>
                )}
                {/* Show composition source indicator */}
                {hasCompositionItems && (
                  <div className="text-xs text-purple-600 font-medium">
                    dont {aggregate.fromComposition} en coffrets
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">

            {isExpanded ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </div>
        </div>


      </button>

      {/* Reservations list */}
      {isExpanded && (
        <div className="border-t bg-gray-50/50">
          <div className="p-3 space-y-2">
            {aggregate.reservations.map((res, idx) => (
              <div
                key={`${res.orderId}-${idx}`}
                className={`rounded-lg p-3 border cursor-pointer transition-all hover:shadow-md ${res.preparedQuantity >= res.quantity
                  ? 'bg-green-50 border-green-200'
                  : 'bg-white border-gray-200'
                  }`}
                onClick={() => onOpenOrder(res.orderId)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center ${res.status === 'ready'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-blue-100 text-blue-700'
                        }`}
                    >
                      <User className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="font-medium">{res.customerName}</div>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Clock className="h-3.5 w-3.5" />
                        <span>{res.pickupTime}</span>
                        <Package className="h-3.5 w-3.5 ml-1" />
                        <span className="font-medium">
                          {(() => {
                            const product = products.find(p => p.id === res.productId);
                            return formatQuantityDisplay(res.quantity, product, res.portionSizeId);
                          })()}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {res.paid && (
                      <Badge className="bg-green-600 text-xs">Payé</Badge>
                    )}
                    {res.status === 'ready' && (
                      <Badge variant="green" className="text-xs">Prête</Badge>
                    )}
                    {res.status === 'pending' && (
                      <Badge variant="secondary" className="text-xs">À préparer</Badge>
                    )}
                    {/* Bouton pour marquer cette ligne comme préparée */}
                    <Button
                      size="sm"
                      variant={res.preparedQuantity >= res.quantity ? "default" : "outline"}
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleLinePrepared(res.orderId, res.lineIndex);
                      }}
                      className={`text-xs h-7 px-2 ${res.preparedQuantity >= res.quantity ? 'bg-green-600 hover:bg-green-700' : ''}`}
                    >
                      {res.preparedQuantity >= res.quantity ? '✓ Préparé' : 'Préparer'}
                    </Button>
                    {/* Bouton de changement de statut commande */}


                  </div>
                </div>
                {/* Options */}
                {(() => {
                  const product = products.find(p => p.id === res.productId);
                  if (!product) return null;
                  const optionLabels = getOptionLabels(
                    { productId: res.productId, quantity: res.quantity, preparedQuantity: 0, options: res.options },
                    product
                  );
                  return optionLabels.length > 0 && (
                    <div className="mt-2 flex gap-2">
                      {optionLabels.map((label, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-300">
                          {label}
                        </Badge>
                      ))}
                    </div>
                  );
                })()}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
